import { noOp, RadioButtonNew, RadioGroupNew } from "@octopusdeploy/design-system-components";
import { DebouncedTextWithLabel as DebounceText, SlugEditor } from "@octopusdeploy/design-system-octopus-components";
import type { PolicyBffResource } from "@octopusdeploy/octopus-server-client";
import { RegoCodeEditor } from "~/areas/platformHub/policies/RegoCodeEditor";
import { ExpandableFormSection, FormSection, required, Summary } from "~/components/form";
interface PolicySettingsTabProps {
    model: PolicyBffResource;
    onChange: (policy: PolicyBffResource) => void;
    getFieldError: (fieldName: string) => string | undefined;
    showPlaceholderAsError?: boolean;
    isStarterPoliciesActive?: boolean;
    placeholders?: string[];
}
export function PolicySettingsTab({ model, onChange, getFieldError, showPlaceholderAsError, isStarterPoliciesActive, placeholders }: PolicySettingsTabProps) {
    const setName = (value: string) => onChange({ ...model, Name: value });
    const setDescription = (value: string) => {
        if (value === "") {
            onChange({ ...model, Description: null });
        }
        else {
            onChange({ ...model, Description: value });
        }
    };
    const setViolationReason = (value: string) => {
        if (value === "") {
            onChange({ ...model, ViolationReason: null });
        }
        else {
            onChange({ ...model, ViolationReason: value });
        }
    };
    const setViolationAction = (value: string) => onChange({ ...model, ViolationAction: value });
    const getViolationActionSummary = () => {
        switch (model.ViolationAction) {
            case "block":
                return Summary.summary("The execution will be stopped from progressing when the policy is non-compliant.");
            case "warn":
                return Summary.summary("The execution will not stop when the policy is non-compliant, but will show a warning.");
            default:
                return Summary.placeholder("Default");
        }
    };
    return (<>
            <ExpandableFormSection errorKey="name" title="Name" focusOnExpandAll summary={model.Name ? Summary.summary(model.Name) : Summary.placeholder("Please enter a name for your policy")} help="A short, memorable, unique name for this policy.">
                <DebounceText value={model.Name} onChange={setName} label="Policy Name" error={getFieldError("Name")} validate={required("Policy name is required.")}/>
                <SlugEditor label="Policy Slug" value={model.Slug} originalSlug={model.Slug} readOnly={true} onChange={noOp}/>
            </ExpandableFormSection>

            <ExpandableFormSection title="Description" errorKey="description" summary={model.Description ? Summary.summary(model.Description) : Summary.placeholder("No policy description provided")} help="Enter a description for this policy.">
                <DebounceText label="Policy description" value={model.Description ?? ""} onChange={setDescription}/>
            </ExpandableFormSection>

            <ExpandableFormSection errorKey="violationReason" title="Violation Reason" summary={model.ViolationReason ? Summary.summary(model.ViolationReason) : Summary.placeholder("No violation reason")} help="The message shown when this policy is non-compliant">
                <DebounceText value={model.ViolationReason ?? ""} onChange={setViolationReason} label="Violation Reason"/>
            </ExpandableFormSection>

            <ExpandableFormSection errorKey="violationAction" title="Violation Action" summary={getViolationActionSummary()} help="The action taken when this policy is non-compliant">
                <RadioGroupNew value={model.ViolationAction} onChange={setViolationAction} label="Voilation Action" hideLabel={true} hideDescription={true} error={getFieldError("violationAction")}>
                    <RadioButtonNew value={"block"} label="Block" name="violationAction" description={"The execution will be stopped from progressing when the policy is non-compliant."} hasDefaultMarker={true}/>
                    <RadioButtonNew value={"warn"} label="Warning" name="violationAction" description={"The execution will not stop when the policy is non-compliant, but will show a warning."}/>
                </RadioGroupNew>
            </ExpandableFormSection>

            <FormSection title="Scope Rego" help="The Rego code that defines what this policy applies to" includeBorder={true} error={getFieldError("scopeRego")}>
                <RegoCodeEditor value={model.ScopeRego} onChange={(rego) => onChange({ ...model, ScopeRego: rego })} error={getFieldError("scopeRego")} showPlaceholderAsError={showPlaceholderAsError}/>
            </FormSection>

            <FormSection title="Conditions Rego" help="The Rego code that defines the policy conditions" includeBorder={true} error={getFieldError("conditionsRego")}>
                <RegoCodeEditor value={model.ConditionsRego} onChange={(rego) => onChange({ ...model, ConditionsRego: rego })} error={getFieldError("conditionsRego")} showPlaceholderAsError={showPlaceholderAsError} placeholders={placeholders}/>
            </FormSection>
        </>);
}
